import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';

export interface UserNotification {
  id: string;
  full_name?: string;
  details?: string;
  reference_link?: string;
  summary?: string;
  created_at?: string;
  read?: boolean;
  group_code?: string;
  sub_category_type?: number;
}

@Injectable()
export class NotificationStateService {
  // ===== Unread order ids (giữ nguyên logic cũ) =====
  private unreadOrderIdsSubject = new BehaviorSubject<string[]>([]);
  unreadOrderIds$ = this.unreadOrderIdsSubject.asObservable();

  // ===== Bell count + User notification list (new) =====
  private bellCountSubject = new BehaviorSubject<number>(0);
  bellCount$ = this.bellCountSubject.asObservable();

  private notificationsSubject = new BehaviorSubject<UserNotification[]>([]);
  notifications$ = this.notificationsSubject.asObservable();

  // ===== Flag "unread new notifications" to trigger the bell notification =====
  private hasNewSubject = new BehaviorSubject<boolean>(false);
  hasNew$ = this.hasNewSubject.asObservable();

  updateUnreadIds(ids: string[]) {
    this.unreadOrderIdsSubject.next(ids);
  }

  getCurrentIds() {
    return this.unreadOrderIdsSubject.value;
  }

  markAsRead(id: string) {
    const currentIds = this.getCurrentIds();
    this.updateUnreadIds(currentIds.filter((currentId) => currentId !== id));
  }

  // ===== New API for user notifications =====
  setBellCount(count: number) {
    this.bellCountSubject.next(count || 0);
    // If count > 0, there is a new notification
    this.hasNewSubject.next((count || 0) > 0);
  }

  setNotifications(list: UserNotification[]) {
    this.notificationsSubject.next(list || []);
  }

  /** Mark a notification as read in the state (after a successful API call to mark it as read) */
  markNotificationRead(id: string) {
    const list = this.notificationsSubject.value.map((n) =>
      n.id === id ? { ...n, read: true } : n
    );
    this.notificationsSubject.next(list);
    // Decrease the bell count by 1 if it is greater than 0
    const current = this.bellCountSubject.value;
    if (current > 0) {
      this.bellCountSubject.next(current - 1);
    }
  }

  /** Mark all as read */
  markAllNotificationsRead() {
    const list = this.notificationsSubject.value.map((n) => ({ ...n, read: true }));
    this.notificationsSubject.next(list);
    this.bellCountSubject.next(0);
    this.hasNewSubject.next(false);
  }

  /**Add a new notification to the top of the list (upon receiving a real-time event)*/
  prependNotification(notification: UserNotification) {
    const list = [notification, ...this.notificationsSubject.value];
    this.notificationsSubject.next(list);
    const current = this.bellCountSubject.value;
    this.bellCountSubject.next(current + 1);
    this.hasNewSubject.next(true);
  }
}
